import { createContext, FC, useContext, useMemo } from 'react';
import { useQuery, UseQueryResult } from 'react-query';
import { useLocalStorage } from 'usehooks-ts';
import { Children } from '../constants/types/prop.types.ts';
import { QueryKeys } from '../api/query.keys.ts';
import UserAPI from '../api/services/user';
import RecruiterAPI from '../api/services/recruiter';
import AgencyAPI from '../api/services/agency';
import {
  IAgencyProfile,
  IAgencyStatistic,
  IMemberCompact,
} from '../api/services/agency/types.ts';
import { IMeData } from '../api/services/user/types.ts';
import { IRecruiterProfile } from '../api/services/recruiter/types.ts';
import { ACCESS_TOKEN_KEY } from '../constants/config.ts';
import { IJobCategory, IJobInfrastructure, IJobSkill } from '../api/services/common/types.ts';
import CommonAPI from '../api/services/common';
import ApplicationsAPI from '../api/services/applications';
import { IApplicationsCountResponse } from '../api/services/applications/types.ts';
import { UserRoles } from '../constants/entities.ts';

interface IStoreItem<T = any>
  extends Pick<UseQueryResult<any>, 'isLoading' | 'isError' | 'refetch'> {
  data: T;
}

interface IGlobalStoreContext {
  user: IStoreItem<IMeData>;
  recruiter: IStoreItem<IRecruiterProfile>;
  agency: IStoreItem<IAgencyProfile>;
  agencyStatistic: IStoreItem<IAgencyStatistic>;
  jobCategories: IStoreItem<IJobCategory[]>;
  jobInfrastructures: IStoreItem<IJobInfrastructure[]>;
  jobSkills: IStoreItem<IJobSkill[]>;
  membersCompactList: IStoreItem<IMemberCompact[]>;
  applicationsCount: IStoreItem<IApplicationsCountResponse>;
}

const GlobalStoreContext = createContext<IGlobalStoreContext>({
  user: {} as IStoreItem<IMeData>,
  recruiter: {} as IStoreItem<IRecruiterProfile>,
  agency: {} as IStoreItem<IAgencyProfile>,
  agencyStatistic: {} as IStoreItem<IAgencyStatistic>,
  jobCategories: {} as IStoreItem<IJobCategory[]>,
  jobInfrastructures: {} as IStoreItem<IJobInfrastructure[]>,
  jobSkills: {} as IStoreItem<IJobSkill[]>,
  membersCompactList: {} as IStoreItem<IMemberCompact[]>,
  applicationsCount: {} as IStoreItem<IApplicationsCountResponse>,
});

const GlobalStoreProvider: FC<Children> = ({ children }) => {
  const [accessToken] = useLocalStorage(ACCESS_TOKEN_KEY, null);

  const baseOptions = {
    retry: false,
    enabled: !!accessToken,
  };

  // Always fetch user data when we have a token
  const userQuery = useQuery(QueryKeys.Me, UserAPI.getMe, baseOptions);

  // Get user data to make conditional queries
  const userData = userQuery.data as IMeData | undefined;
  const isUserLoaded = !userQuery.isLoading && !!userData;

  // Extract common condition patterns to avoid duplication
  const hasBasicAccess = !!accessToken && isUserLoaded;
  const hasEmailConfirmedAccess = hasBasicAccess && userData?.emailConfirmed;
  const hasPersonalProfileCompleted = hasEmailConfirmedAccess && userData?.personalProfileCompleted;
  const hasAgencyAccess = hasEmailConfirmedAccess && userData?.isAgencySet;
  const isMember = hasPersonalProfileCompleted && (
    userData?.role === UserRoles.Recruiter || userData?.role === UserRoles.AgencyManager
  );

  const isAdmin = userData?.role === UserRoles.Admin;

  // Only fetch recruiter data if user is loaded and has the right role
  const recruiterQuery = useQuery(
    QueryKeys.RecruiterMe,
    RecruiterAPI.getMyProfile,
    {
      ...baseOptions,
      enabled: isMember,
    },
  );

  // Only fetch agency data if user is loaded and email is confirmed
  const agencyQuery = useQuery(
    QueryKeys.AgencyMe,
    AgencyAPI.getMyAgency,
    {
      ...baseOptions,
      enabled: hasAgencyAccess && !isAdmin,
    },
  );

  // Only fetch agency statistics if user is loaded, email confirmed, and agency is set
  const agencyStatisticQuery = useQuery(
    QueryKeys.AgencyStatistic,
    AgencyAPI.getMyAgencyStatistic,
    {
      ...baseOptions,
      enabled: hasAgencyAccess && !isAdmin,
    },
  );

  // Job-related queries can be fetched when user is loaded and email confirmed
  const jobCategoriesQuery = useQuery(
    QueryKeys.JobCategories,
    CommonAPI.getJobCategories,
    {
      ...baseOptions,
      enabled: hasEmailConfirmedAccess && !isAdmin,
    },
  );

  const jobInfrastructuresQuery = useQuery(
    QueryKeys.JobInfrastructures,
    CommonAPI.getJobInfrastructures,
    {
      ...baseOptions,
      enabled: hasEmailConfirmedAccess && !isAdmin,
    },
  );

  const jobSkillsQuery = useQuery(
    QueryKeys.JobSkills,
    CommonAPI.getJobSkills,
    {
      ...baseOptions,
      enabled: hasEmailConfirmedAccess && !isAdmin,
    },
  );

  // Only fetch members if user is loaded, email confirmed, and agency is set
  const membersCompactQuery = useQuery(
    QueryKeys.MembersCompactList,
    AgencyAPI.getMembersCompact,
    {
      ...baseOptions,
      enabled: hasAgencyAccess && !isAdmin,
    },
  );

  // Only fetch applications count if user is loaded and email confirmed
  const applicationsCountQuery = useQuery(
    QueryKeys.ApplicationsCount,
    () => ApplicationsAPI.getApplicationsCount(),
    {
      ...baseOptions,
      enabled: hasAgencyAccess && !isAdmin,
    },
  );

  const dependencies = [
    userQuery,
    recruiterQuery,
    agencyQuery,
    agencyStatisticQuery,
    jobCategoriesQuery,
    jobInfrastructuresQuery,
    jobSkillsQuery,
    membersCompactQuery,
    applicationsCountQuery,
  ];

  const value = useMemo(
    () => ({
      user: {
        ...userQuery,
        data: (userQuery.data ?? {}) as IMeData,
      },
      recruiter: {
        ...recruiterQuery,
        data: (recruiterQuery.data ?? {
          firstName: '',
          lastName: '',
          jobTitle: '',
          avatarSignedUrl: '',
          imageKey: null,
        }) as IRecruiterProfile,
      },
      agency: {
        ...agencyQuery,
        data: (agencyQuery.data ?? {}) as IAgencyProfile,
      },

      agencyStatistic: {
        ...agencyStatisticQuery,
        data: (agencyStatisticQuery.data ?? {}) as IAgencyStatistic,
      },

      jobCategories: {
        ...jobCategoriesQuery,
        data: (jobCategoriesQuery.data ?? []) as IJobCategory[],
      },
      jobInfrastructures: {
        ...jobInfrastructuresQuery,
        data: (jobInfrastructuresQuery.data ?? []) as IJobCategory[],
      },
      jobSkills: {
        ...jobSkillsQuery,
        data: (jobSkillsQuery.data ?? []) as IJobSkill[],
      },
      membersCompactList: {
        ...membersCompactQuery,
        data: (membersCompactQuery.data ?? []) as IMemberCompact[],
      },
      applicationsCount: {
        ...applicationsCountQuery,
        data: applicationsCountQuery.data ?? { statusBreakdown: [], total: 0 },
      },
    }),
    dependencies.map(({ isLoading, data }) => [data, isLoading]).flat(1),
  );

  return <GlobalStoreContext.Provider value={value}>{children}</GlobalStoreContext.Provider>;
};

export default GlobalStoreProvider;

export const useGlobalStore = () => useContext(GlobalStoreContext);
